import type { NavigationVisibility, NavItem, RolePermission } from './types'
import { isValidElement, type ReactElement } from 'react'
import type { RouteObject } from 'react-router-dom'
import { Guard } from '../../router/guard'
import { type Role, roleCodeMap } from '../../domain/role.enum'
import { navigation } from '../../layout/home/components/menu'
import { DefaultRedirect } from '../../router/default-redirect'
import type { Permission } from '../../domain/permission.enum'
import { routes } from '../../router'

interface GuardProps {
  element: React.ReactNode
  permissions?: Permission | Permission[]
  roles?: Role | Role[]
}

const sameRoles = (a: Array<Role>, b: Array<Role>): boolean => {
  if (a.length !== b.length) {
    return false
  }

  const sa = [...a].sort()
  const sb = [...b].sort()

  return sa.every((v, i) => v === sb[i])
}

const buildNavigationIndex = () => {
  const toKey = (p?: string) => {
    if (!p) {
      return ''
    }

    const trimmed = p.replace(/^\/+|\/+$/g, '')

    return `/${trimmed}`
  }

  const index = new Map<string, { roles: Array<Role>; title?: string }>()

  const visit = (items: Array<NavItem>) => {
    for (const it of items) {
      const rawPath = it.path ?? it.href ?? it.to
      const key = toKey(rawPath)

      if (key) {
        index.set(key, { roles: Array.isArray(it.roles) ? it.roles : [], title: it.title })
      }

      if (it.children?.length) {
        visit(it.children)
      }
    }
  }

  if (Array.isArray(navigation)) {
    visit(navigation as unknown as Array<NavItem>)
  }

  return index
}

const NAV_INDEX = buildNavigationIndex()

const buildNavigationOrder = () => {
  const order: Array<string> = []

  const toKey = (p?: string) => {
    if (!p) {
      return ''
    }

    const trimmed = p.replace(/^\/+|\/+$/g, '')

    return `/${trimmed}`
  }

  const visit = (items: Array<NavItem>) => {
    for (const it of items) {
      const rawPath = it.path ?? it.href ?? it.to
      const key = toKey(rawPath)

      if (key) {
        order.push(key)
      }

      if (it.children?.length) {
        visit(it.children)
      }
    }
  }

  visit(navigation)

  return order
}

const NAV_ORDER = buildNavigationOrder()
const NAV_ORDER_MAP = new Map<string, number>(NAV_ORDER.map((p, i) => [p, i]))

const resolveNavigationVisibilityFromMenu = (
  path: string,
  roles: Array<Role>
): NavigationVisibility => {
  const navItem = NAV_INDEX.get(path)

  if (!navItem) {
    return { value: 'NOT IN MENU', color: 'red' }
  }

  if (sameRoles(roles, navItem.roles)) {
    return { value: 'VISIBLE', color: 'green' }
  }

  const navRolesString = navItem.roles.map(role => roleCodeMap[role]).join(', ')

  return { value: `MENU ROLES: ${navRolesString}`, color: 'orange' }
}

export const getAllRolePermissions = (): Array<RolePermission> => {
  const permissions: Array<RolePermission> = []

  const trimSlashes = (p: string) => p.replace(/^\/+|\/+$/g, '')
  const joinPaths = (base: string, segment: string) => {
    if (segment === '*') {
      return base ? `${trimSlashes(base)}/*` : '*'
    }

    const b = trimSlashes(base)
    const s = trimSlashes(segment)

    return b ? `${b}/${s}` : s
  }
  const toAbsolute = (p: string) => {
    if (p === '*') {
      return '/*'
    }

    const t = trimSlashes(p)

    return t ? `/${t}` : '/'
  }

  const getPageName = (node: unknown): string => {
    if (isValidElement(node) && node.type) {
      const { displayName, name } = node.type as never
      return displayName || name || 'Unknown'
    }

    return 'Unknown'
  }

  const normalizeRoles = (rolesProp: unknown): Array<Role> => {
    if (!rolesProp) {
      return []
    }

    return Array.isArray(rolesProp) ? (rolesProp as Array<Role>) : [rolesProp as Role]
  }

  const isDefaultRedirectElement = (node: unknown): boolean =>
    isValidElement(node) && node.type === DefaultRedirect

  const walk = (nodes: Array<RouteObject>, base: string) => {
    for (const r of nodes) {
      const seg = r.path ?? ''
      const full = seg ? joinPaths(base, seg) : base

      if (isValidElement(r.element) && r.element.type === Guard) {
        const guardEl = r.element as ReactElement<GuardProps>

        if (isDefaultRedirectElement(guardEl.props.element)) {
          continue
        }

        const roles = normalizeRoles(guardEl.props.roles)
        const path = r.index ? toAbsolute(base) : toAbsolute(full)
        const navItem = NAV_INDEX.get(path)
        const pageName = navItem?.title ?? getPageName(guardEl.props.element)

        permissions.push({
          page: pageName,
          path,
          navigationVisibility: resolveNavigationVisibilityFromMenu(path, roles),
          roles
        })
      }

      if (r.children && r.children.length) {
        walk(r.children, full)
      }
    }
  }

  walk(routes, '')

  const seen = new Set<string>()

  const uniq = permissions.filter(p => {
    if (seen.has(p.path)) {
      return false
    }

    seen.add(p.path)

    return true
  })

  uniq.sort((a, b) => {
    const ia = NAV_ORDER_MAP.has(a.path)
      ? (NAV_ORDER_MAP.get(a.path) as number)
      : Number.POSITIVE_INFINITY
    const ib = NAV_ORDER_MAP.has(b.path)
      ? (NAV_ORDER_MAP.get(b.path) as number)
      : Number.POSITIVE_INFINITY

    if (ia !== ib) {
      return ia - ib
    }

    if (a.path !== b.path) {
      return a.path.localeCompare(b.path)
    }

    return a.page.localeCompare(b.page)
  })

  return uniq
}
